import type { ReactNode } from "react";

// Lightweight markdown for Moo replies. Escapes HTML and supports the subset the assistant
// actually uses — paragraphs, emphasis, inline code, and lists — without a parser dependency.

function inline(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  // Code, bold, italic — code first so asterisks inside backticks stay literal.
  const pattern = /(`+)(.+?)\1|\*\*(.+?)\*\*|\*(.+?)\*|_(.+?)_/g;
  let last = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = pattern.exec(text))) {
    if (match.index > last) nodes.push(text.slice(last, match.index));
    if (match[2] != null) nodes.push(<code key={key++}>{match[2]}</code>);
    else if (match[3] != null) nodes.push(<strong key={key++}>{match[3]}</strong>);
    else nodes.push(<em key={key++}>{match[4] ?? match[5]}</em>);
    last = match.index + match[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

function isBullet(line: string) {
  return /^[-*]\s+/.test(line);
}
function isOrdered(line: string) {
  return /^\d+\.\s+/.test(line);
}
function listItemText(line: string) {
  return line.replace(/^([-*]|\d+\.)\s+/, "");
}

/** Turn Moo's markdown reply into safe React nodes. */
export function MooMarkdown({ text }: { text: string }) {
  const blocks = text.replace(/\r\n/g, "\n").trim().split(/\n{2,}/);
  return (
    <div className="moo-text moo-markdown">
      {blocks.map((block, i) => {
        const lines = block.split("\n");
        if (lines.every((line) => isBullet(line) || isOrdered(line))) {
          const ordered = isOrdered(lines[0]);
          const List = ordered ? "ol" : "ul";
          return (
            <List key={i}>
              {lines.map((line, j) => (
                <li key={j}>{inline(listItemText(line))}</li>
              ))}
            </List>
          );
        }
        if (/^#{1,3}\s+/.test(lines[0]) && lines.length === 1) {
          const level = Math.min(3, lines[0].match(/^#+/)![0].length);
          const Tag = (`h${level + 2}` as "h3" | "h4" | "h5");
          return <Tag key={i}>{inline(lines[0].replace(/^#{1,3}\s+/, ""))}</Tag>;
        }
        return (
          <p key={i}>
            {lines.map((line, j) => (
              <span key={j}>
                {j > 0 && <br />}
                {inline(line)}
              </span>
            ))}
          </p>
        );
      })}
    </div>
  );
}
